import { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { Link } from "react-router-dom";
import { showLowConfidenceFindingsAtom } from "../../atoms/reporterPrefsAtoms";
import type { ReviewerDisplayMode } from "../../atoms/reviewerPrefsAtoms";
import { FaCog } from "../../utils/icons";
import { ReviewerDisplayToggle } from "./ReviewerDisplayToggle";

interface Props {
  mode: ReviewerDisplayMode;
  onChangeMode: (mode: ReviewerDisplayMode) => void;
  canEditTemplates: boolean;
}

export function ReviewerSettingsMenu({ mode, onChangeMode, canEditTemplates }: Props) {
  const [open, setOpen] = useState(false);
  const [showLowConfidence, setShowLowConfidence] = useAtom(showLowConfidenceFindingsAtom);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="report-settings" ref={ref}>
      <button
        type="button"
        className={`report-btn report-btn--icon ${open ? "report-btn--toggle-active" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Report settings"
        aria-label="Report settings"
        onClick={() => setOpen((v) => !v)}
      >
        <FaCog className="w-4 h-4" />
      </button>
      {open && (
        <div className="report-settings__panel" role="menu">
          <span className="report-settings__section-label">Display</span>
          <ReviewerDisplayToggle mode={mode} onChange={onChangeMode} />
          {mode === "inline" && (
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={showLowConfidence}
              className="report-settings__item"
              onClick={() => setShowLowConfidence((v) => !v)}
            >
              <span>Show review-tier findings</span>
              <span aria-hidden="true">{showLowConfidence ? "On" : "Off"}</span>
            </button>
          )}
          {canEditTemplates && (
            <Link
              to="/my-templates"
              role="menuitem"
              className="report-settings__item"
              onClick={() => setOpen(false)}
            >
              <span>Templates</span>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
